import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import FormField, { Input, Textarea, Select } from '../../components/FormField';

const EMPTY = {
  name: '', description: '', location: '', start_date: '', end_date: '',
  ticket_price: '', max_participants: '', seat_count: 0, status: 'draft',
  ticket_sale_start: '', ticket_sale_end: '', seat_booking_start: '',
  paypal_info: '', paypal_me: '',
  bank_info: '', bank_iban: '', bank_bic: '', bank_holder: '', bank_enabled: true,
  rules: '', agb: '', faq: '',
};
const toInputDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};
const STATUSES = ['draft','published','ticket_sale_active','sold_out','finished','archived'];

function Toggle({ value, onChange }) {
  return (
    <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => onChange(!value)}>
      <span className="text-xs text-slate-500">{value ? 'Aktiv' : 'Deaktiviert'}</span>
      <div className={`relative w-10 h-5 rounded-full transition-colors ${value ? 'bg-emerald-500' : 'bg-slate-300'}`}>
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </div>
    </div>
  );
}

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = () => api.get('/api/events/all').then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const set = (f) => (e) => setForm((prev) => ({ ...prev, [f]: e.target.value }));
  const setVal = (f, v) => setForm((prev) => ({ ...prev, [f]: v }));

  const openEdit = (ev) => {
    setEditing(ev?.id || 'new');
    setForm(ev ? {
      ...EMPTY,
      name: ev.name || '',
      description: ev.description || '',
      location: ev.location || '',
      start_date: toInputDate(ev.start_date),
      end_date: toInputDate(ev.end_date),
      ticket_price: ev.ticket_price || '',
      max_participants: ev.max_participants || '',
      seat_count: ev.seat_count ?? 0,
      status: ev.status || 'draft',
      ticket_sale_start: toInputDate(ev.ticket_sale_start),
      ticket_sale_end: toInputDate(ev.ticket_sale_end),
      seat_booking_start: toInputDate(ev.seat_booking_start),
      paypal_info: ev.paypal_info || '',
      paypal_me: ev.paypal_me || '',
      bank_info: ev.bank_info || '',
      bank_iban: ev.bank_iban || '',
      bank_bic: ev.bank_bic || '',
      bank_holder: ev.bank_holder || '',
      bank_enabled: ev.bank_enabled ?? true,
      rules: ev.rules || '',
      agb: ev.agb || '',
      faq: ev.faq || '',
    } : EMPTY);
    setError('');
    setSaved(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSaved(false);
    try {
      const payload = { ...form };
      ['ticket_price','max_participants','seat_count'].forEach((k) => { if (payload[k] === '') payload[k] = null; });
      ['start_date','end_date','ticket_sale_start','ticket_sale_end','seat_booking_start'].forEach((k) => { if (payload[k] === '') payload[k] = null; });
      ['bank_iban','bank_bic','bank_holder','paypal_me','bank_info','paypal_info'].forEach((k) => { if (payload[k] === '') payload[k] = null; });
      if (editing === 'new') {
        await api.post('/api/events', payload);
        setEditing(null);
      } else {
        await api.put(`/api/events/${editing}`, payload);
        setSaved(true);
      }
      load();
    } catch (err) { setError(err.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Event wirklich löschen?')) return;
    try { await api.delete(`/api/events/${id}`); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Event konnte nicht gelöscht werden.'); }
  };

  const activate = async (id) => {
    if (!window.confirm('Dieses Event als DAS aktuelle Event auf der Hauptseite anzeigen? Ein zuvor aktives Event wird automatisch deaktiviert.')) return;
    try { await api.post(`/api/events/${id}/activate`); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Aktivieren fehlgeschlagen'); }
  };

  const deactivate = async (id) => {
    try { await api.post(`/api/events/${id}/deactivate`); load(); }
    catch (err) { setError(err.response?.data?.detail || 'Deaktivieren fehlgeschlagen'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="page-title mb-0">Events</h2>
          <p className="text-xs text-slate-500 mt-1">Nur das aktive Event ist für Besucher auf der Hauptseite sichtbar. Alle anderen erscheinen automatisch im öffentlichen Archiv.</p>
        </div>
        <Button onClick={() => openEdit(null)}>+ Neues Event</Button>
      </div>

      {error && !editing && <div className="alert-error mb-4">{error}</div>}

      {editing && (
        <div className="card border border-ocean-200 mb-6">
          <h3 className="section-title">{editing === 'new' ? 'Event erstellen' : 'Event bearbeiten'}</h3>
          {error && <div className="alert-error mb-4">{error}</div>}
          {saved && <div className="alert-success mb-4">Änderungen gespeichert.</div>}
          <form onSubmit={save}>
            <div className="grid grid-cols-2 gap-x-4">
              <FormField label="Name" required className="col-span-2"><Input value={form.name} onChange={set('name')} required /></FormField>
              <FormField label="Ort"><Input value={form.location} onChange={set('location')} /></FormField>
              <FormField label="Status"><Select value={form.status} onChange={set('status')}>{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</Select></FormField>
              <FormField label="Start"><Input type="datetime-local" value={form.start_date} onChange={set('start_date')} /></FormField>
              <FormField label="Ende"><Input type="datetime-local" value={form.end_date} onChange={set('end_date')} /></FormField>
              <FormField label="Ticketpreis (€)"><Input type="number" step="0.01" value={form.ticket_price} onChange={set('ticket_price')} /></FormField>
              <FormField label="Max. Teilnehmer"><Input type="number" value={form.max_participants} onChange={set('max_participants')} /></FormField>
              <FormField label="Verkauf ab"><Input type="datetime-local" value={form.ticket_sale_start} onChange={set('ticket_sale_start')} /></FormField>
              <FormField label="Verkauf bis"><Input type="datetime-local" value={form.ticket_sale_end} onChange={set('ticket_sale_end')} /></FormField>
              <FormField label="Sitzplatzbuchung ab"><Input type="datetime-local" value={form.seat_booking_start} onChange={set('seat_booking_start')} /></FormField>
              <FormField label="Sitzplätze"><Input type="number" value={form.seat_count} onChange={set('seat_count')} /></FormField>
            </div>
            <FormField label="Beschreibung"><Textarea value={form.description} onChange={set('description')} /></FormField>

            {/* PayPal */}
            <div className="border border-slate-200 rounded-xl p-4 mb-4 space-y-3">
              <p className="text-sm font-semibold text-slate-700">PayPal</p>
              <FormField label="PayPal.me-Benutzername" description="Nur den Usernamen eingeben — QR-Code und Zahlungslink werden automatisch erstellt">
                <Input value={form.paypal_me} onChange={set('paypal_me')} placeholder="z.B. BeachLAN2026" />
              </FormField>
              <FormField label="Zusätzliche PayPal-Info" description="Optionaler Hinweistext (z.B. E-Mail-Adresse)">
                <Textarea value={form.paypal_info} onChange={set('paypal_info')} rows={2} />
              </FormField>
            </div>

            {/* Bankverbindung */}
            <div className="border border-slate-200 rounded-xl p-4 mb-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">Banküberweisung</p>
                <Toggle value={form.bank_enabled} onChange={(v) => setVal('bank_enabled', v)} />
              </div>
              <FormField label="Kontoinhaber" description="Wird im EPC-QR-Code verwendet">
                <Input value={form.bank_holder} onChange={set('bank_holder')} placeholder="z.B. Max Mustermann" />
              </FormField>
              <div className="grid grid-cols-2 gap-x-4">
                <FormField label="IBAN" description="Für automatischen QR-Code">
                  <Input value={form.bank_iban} onChange={set('bank_iban')} placeholder="DE89 3704 0044 0532 0130 00" />
                </FormField>
                <FormField label="BIC" description="Optional (SEPA)">
                  <Input value={form.bank_bic} onChange={set('bank_bic')} placeholder="z.B. COBADEFFXXX" />
                </FormField>
              </div>
              <FormField label="Zusätzliche Info" description="Optionaler Hinweistext">
                <Textarea value={form.bank_info} onChange={set('bank_info')} rows={2} />
              </FormField>
            </div>

            <FormField label="Regeln"><Textarea value={form.rules} onChange={set('rules')} /></FormField>
            <FormField label="AGB"><Textarea value={form.agb} onChange={set('agb')} /></FormField>
            <FormField label="FAQ"><Textarea value={form.faq} onChange={set('faq')} /></FormField>
            <div className="flex gap-2">
              <Button type="submit" loading={loading}>Speichern</Button>
              <Button variant="ghost" type="button" onClick={() => { setEditing(null); setSaved(false); }}>Abbrechen</Button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {events.map((ev) => (
          <div key={ev.id} className="card flex flex-wrap gap-3 justify-between items-center">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-ocean-900">{ev.name}</span>
                <Badge status={ev.status} />
                {ev.is_active && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-500 text-white">
                    Aktuelles Event
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">{ev.location || '–'} · {ev.tickets_sold || 0}/{ev.max_participants || '∞'} Tickets · {ev.seats_booked || 0} Sitze</p>
            </div>
            <div className="flex gap-2">
              {ev.is_active
                ? <Button variant="secondary" size="sm" onClick={() => deactivate(ev.id)}>Deaktivieren</Button>
                : <Button variant="success" size="sm" onClick={() => activate(ev.id)}>Aktivieren</Button>}
              <Button variant="secondary" size="sm" onClick={() => openEdit(ev)}>Bearbeiten</Button>
              <Button variant="danger" size="sm" onClick={() => del(ev.id)}>Löschen</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
